import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collateralApi, ApiError } from '../services/collateralApi';

export default function useCreditRightBatchPageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const [statusCounts, setStatusCounts] = useState<Record<string, any>>({});
    const [pageSize, setPageSize] = useState(100);

    const requestSeq = useRef(0);

    const fetchData = useCallback(async () => {
        const requestId = ++requestSeq.current;
        setIsLoading(true);
        try {
            const result = await collateralApi.getCreditRightBatches({
                page, pageSize,
                status: statusFilter || undefined,
                search: search || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            if (requestId !== requestSeq.current) return;
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
            setStatusCounts(result.statusCounts || {});
        } catch (e: any) {
            if (requestId !== requestSeq.current) return;
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            if (requestId === requestSeq.current) {
                setIsLoading(false);
                setInitialLoading(false);
            }
        }
    }, [page, pageSize, statusFilter, search, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const changePageSize = (size: number) => { setPageSize(size); setPage(1); };

    return {
        rows, total, page, setPage, pageSize, changePageSize,
        isLoading, initialLoading, apiInfo, statusCounts,
        statusFilter, setStatusFilter,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        refresh,
    };
}
